/* eslint-disable no-undef */
import { useEffect, useRef, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import { DrawingMode } from './MapDraw.types'

const SOURCE_ID = 'measure-distance-line'
const LAYER_ID = 'measure-distance-line-layer'
const POINTS_SOURCE_ID = 'measure-distance-points'
const POINTS_LAYER_ID = 'measure-distance-points-layer'

const calculateDistance = (coord1: [number, number], coord2: [number, number]): number => {
  const [lon1, lat1] = coord1
  const [lon2, lat2] = coord2
  const R = 6371000
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

const formatDistance = (meters: number): string => {
  if (meters < 1000) return `${meters.toFixed(1)}m`
  return `${(meters / 1000).toFixed(2)}km`
}

const TOOLTIP_STYLES = [
  'position:fixed',
  'pointer-events:none',
  'z-index:10000',
  'background:rgba(50,50,50,0.92)',
  'color:#fff',
  'border-radius:8px',
  'padding:4px 8px',
  'font-size:13px',
  'font-weight:500',
  'white-space:nowrap',
  'backdrop-filter:blur(4px)',
  'transform:translate(16px,-50%)',
  'display:none',
].join(';')

interface UseMeasureDistanceParams {
  map: mapboxgl.Map | null
  currentMode: DrawingMode
  enabled: boolean
}

export const useMeasureDistance = ({
  map,
  currentMode,
  enabled,
}: UseMeasureDistanceParams): void => {
  const pointsRef = useRef<[number, number][]>([])
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  const getTooltip = useCallback(() => {
    if (!tooltipRef.current) {
      const el = document.createElement('div')
      el.setAttribute('style', TOOLTIP_STYLES)
      document.body.appendChild(el)
      tooltipRef.current = el
    }
    return tooltipRef.current
  }, [])

  const hideTooltip = useCallback(() => {
    if (tooltipRef.current) {
      tooltipRef.current.style.display = 'none'
    }
  }, [])

  const updateLine = useCallback(
    (coords: [number, number][], preview?: [number, number]) => {
      if (!map) return
      const lineCoords = preview ? [...coords, preview] : coords

      const lineData: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: lineCoords },
      }

      const pointData: GeoJSON.FeatureCollection<GeoJSON.Point> = {
        type: 'FeatureCollection',
        features: coords.map((c) => ({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: c },
        })),
      }

      const lineSource = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource
      if (lineSource) {
        lineSource.setData(
          lineCoords.length >= 2 ? lineData : { type: 'FeatureCollection', features: [] }
        )
      }

      const pointSource = map.getSource(POINTS_SOURCE_ID) as mapboxgl.GeoJSONSource
      if (pointSource) {
        pointSource.setData(pointData)
      }
    },
    [map]
  )

  const getTotalDistance = useCallback((points: [number, number][], cursor?: [number, number]) => {
    const allPoints = cursor ? [...points, cursor] : points
    let total = 0
    for (let i = 0; i < allPoints.length - 1; i++) {
      total += calculateDistance(allPoints[i], allPoints[i + 1])
    }
    return total
  }, [])

  const cleanup = useCallback(() => {
    if (!map) return
    hideTooltip()
    pointsRef.current = []

    if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID)
    if (map.getLayer(POINTS_LAYER_ID)) map.removeLayer(POINTS_LAYER_ID)
    if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
    if (map.getSource(POINTS_SOURCE_ID)) map.removeSource(POINTS_SOURCE_ID)
  }, [map, hideTooltip])

  useEffect(() => {
    if (!map || !enabled || currentMode !== 'measure') {
      if (map && currentMode !== 'measure') cleanup()
      return
    }

    // Add sources and layers
    if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (!map.getSource(POINTS_SOURCE_ID)) {
      map.addSource(POINTS_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (!map.getLayer(LAYER_ID)) {
      map.addLayer({
        id: LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#0294df', 'line-width': 3 },
      })
    }
    if (!map.getLayer(POINTS_LAYER_ID)) {
      map.addLayer({
        id: POINTS_LAYER_ID,
        type: 'circle',
        source: POINTS_SOURCE_ID,
        paint: { 'circle-radius': 5, 'circle-color': '#0294df' },
      })
    }

    pointsRef.current = []

    const handleClick = (e: mapboxgl.MapMouseEvent) => {
      const point: [number, number] = [e.lngLat.lng, e.lngLat.lat]
      pointsRef.current.push(point)
      updateLine(pointsRef.current)
    }

    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const points = pointsRef.current
      if (points.length === 0) return

      const cursor: [number, number] = [e.lngLat.lng, e.lngLat.lat]
      updateLine(points, cursor)

      const total = getTotalDistance(points, cursor)
      const rect = map.getContainer().getBoundingClientRect()
      const tooltip = getTooltip()
      tooltip.textContent = formatDistance(total)
      tooltip.style.left = `${e.point.x + rect.left}px`
      tooltip.style.top = `${e.point.y + rect.top}px`
      tooltip.style.display = 'block'
    }

    const handleDblClick = (e: mapboxgl.MapMouseEvent) => {
      e.preventDefault()
      // Measurements are temporary, so finishing one removes it from the map.
      pointsRef.current = []
      updateLine([])
      hideTooltip()
    }

    const canvas = map.getCanvas()
    canvas.style.cursor = 'crosshair'
    map.on('click', handleClick)
    map.on('mousemove', handleMouseMove)
    map.on('dblclick', handleDblClick)
    canvas.addEventListener('mouseout', hideTooltip)

    return () => {
      canvas.style.cursor = ''
      map.off('click', handleClick)
      map.off('mousemove', handleMouseMove)
      map.off('dblclick', handleDblClick)
      canvas.removeEventListener('mouseout', hideTooltip)
      hideTooltip()
    }
  }, [map, enabled, currentMode, updateLine, getTotalDistance, getTooltip, hideTooltip, cleanup])

  // Cleanup DOM on unmount
  useEffect(() => {
    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove()
        tooltipRef.current = null
      }
    }
  }, [])
}
